import { FUN_CURRENCY_CODE, getUserCurrency, SOCIAL_CURRENCY_CODE } from './currency';
import { triggerDeprecation } from './helpers';

import Config from 'Config/Config';

const defaultCurrency = Config.get('app.currency');
const locale = document.getElementsByTagName('html')[0].getAttribute('lang') ?? Config.get('app.locale');

export function getFractionDigits() {
    return parseInt(process.env.MIX_FRACTION_DIGITS) || 2;
}

export function formatUserCurrency(amount: number | bigint) {
    return formatCurrency(amount, 2, getUserCurrency());
}

export function formatCurrency(amount: number | bigint, digitsInput = null, currencyCode: string = '') {
    const digits = digitsInput ?? getFractionDigits();
    let result = '';

    if (currencyCode === '') {
        triggerDeprecation(
            'Using formatCurrency without currency code provided is deprecated. Using default currency as fallback.'
        );
    }

    const currency = currencyCode !== '' ? currencyCode : defaultCurrency;
    if (amount != null) {
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            maximumFractionDigits: digits,
            minimumFractionDigits: digits,
        });

        switch (currency) {
            case 'ZMW':
                result = formatCurrencyForZMW(formatter, amount);
                break;

            case 'MWK':
                result = formatCurrencyForMWK(formatter, amount);
                break;

            case 'MZN':
                result = formatCurrencyForMZN(formatter, amount);
                break;

            case 'PEN':
                result = formatCurrencyForPEN(formatter, amount);
                break;

            case 'CDF':
                result = formatCurrencyForCDF(formatter, amount);
                break;

            case 'GTQ':
                result = formatCurrencyForGTQ(formatter, amount);
                break;

            case 'XOF':
            case 'XAF':
                result = formatCurrencyFCFA(formatter, amount);
                break;

            case 'GMD':
                result = formatter.format(amount).replace('GMD', 'D');
                break;

            default:
                result = formatter.format(amount);
                break;
        }
    }

    return result;
}

function formatCurrencyForMWK(formatter: Intl.NumberFormat, amount: number | bigint) {
    const parts = formatter.formatToParts(amount);

    // Take two first parts: currency code, separator and move them to the end
    return (
        parts
            .slice(2, parts.length)
            .map((item) => item.value)
            .join('') +
        parts
            .slice(0, 2)
            .reverse()
            .map((item) => item.value)
            .join('')
    );
}

function formatCurrencyForZMW(formatter: Intl.NumberFormat, amount: number | bigint) {
    return formatter
        .formatToParts(amount)
        .map(({ type, value }) => {
            if (type === 'literal') {
                return '';
            }

            return value;
        })
        .reduce((string, part) => string + part);
}

function formatCurrencyForMZN(formatter: Intl.NumberFormat, amount: number | bigint) {
    const parts = formatter.formatToParts(amount).map(({ type, value }) => {
        if (type === 'currency') {
            return 'MT';
        }

        return value;
    });

    if (locale === 'pt-MZ') {
        return parts.join('');
    }

    // Take two first parts: currency code, separator and move them to the end
    return parts.slice(2, parts.length).join('') + parts.slice(0, 2).reverse().join('');
}

function formatCurrencyForPEN(formatter: Intl.NumberFormat, amount: number | bigint) {
    return formatter.format(amount).replace(/[\u00A0\u202F]/g, ' ');
}

function formatCurrencyForCDF(formatter: Intl.NumberFormat, amount: number | bigint) {
    const parts = formatter.formatToParts(amount).map(({ type, value }) => {
        if (type === 'currency') {
            return 'FC';
        }

        return value;
    });

    return parts.join('');
}

function formatCurrencyForGTQ(formatter: Intl.NumberFormat, amount: number | bigint) {
    const parts = formatter.formatToParts(amount).map(({ type, value }) => {
        if (type === 'currency') {
            return 'Q';
        }

        return value;
    });

    return parts.join('');
}

function formatCurrencyFCFA(formatter: Intl.NumberFormat, amount: number | bigint) {
    return formatter
        .formatToParts(amount)
        .map(({ type, value }) => (type === 'currency' ? 'FCFA' : value))
        .join('');
}

export function formatCurrencyCustom(amount: number, currencyCode: string): string {
    if ([FUN_CURRENCY_CODE, SOCIAL_CURRENCY_CODE].includes(currencyCode)) {
        return `${amount} ${currencyCode}`;
    }

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
    });

    return formatter.format(amount);
}

export function formatCurrencyWithLetterPrefixSpacing(
    amount: number | bigint,
    digitsInput = null,
    currencyCode: string = ''
) {
    return formatCurrency(amount, digitsInput, currencyCode).replace(/^([A-Za-z]+)(\d)/, '$1 $2');
}
